/**
 * The spine.
 *
 * Everything in this app hangs off `course → chapter → task → attempt`. A chapter is defined by
 * a RANGE of task numbers, because that is how the books are actually organised and how the
 * owner actually works: "chapter 1 is 1001–1100, I do 1001, then 1004, then 1007."
 *
 * Tasks are not pre-created. A task record appears the first time you open its number, so a
 * chapter of 120 exercises costs nothing until you touch it.
 */

export type Locale = 'sv' | 'en' | 'mixed';

export interface Bilingual {
  sv: string;
  en: string;
}

export const say = (text: Bilingual, locale: Locale): string =>
  locale === 'sv' ? text.sv : text.en;

export type CourseKind = 'gymnasium' | 'university';

export interface Course {
  id: string;
  /** Short code shown on the chapter map: "Ma4", "LinAlg". */
  code: string;
  name: Bilingual;
  kind: CourseKind;
  /** The book this course's numbering follows, free text: "Matematik 5000+ 4". */
  book: string;
  /** One of --curve-1…4, so a course keeps its colour everywhere it appears. */
  accent: 1 | 2 | 3 | 4;
  order: number;
  createdAt: number;
}

export interface Chapter {
  id: string;
  courseId: string;
  /** Chapter number as written in the book — "1", "4", "A2". */
  number: string;
  title: string;
  /** The task-number range this chapter covers, inclusive. */
  from: number;
  to: number;
  order: number;
  createdAt: number;
}

/** Where a task ended up. `wrong` is not a failure state — it is the most useful one. */
export type TaskStatus = 'untouched' | 'doing' | 'done' | 'wrong' | 'stuck';

export const TASK_STATUSES: readonly TaskStatus[] = ['untouched', 'doing', 'done', 'wrong', 'stuck'];

export interface Task {
  id: string;
  chapterId: string;
  courseId: string;
  /** The number as the book writes it. Kept as a string so "4.2.13" works as well as "1001". */
  number: string;
  /** The numeric key used for ordering and range membership. */
  index: number;
  title: string;
  status: TaskStatus;
  /** Difficulty tier as the Swedish books mark it. */
  tier: 'a' | 'b' | 'c' | '';
  /** Minutes actually spent, accumulated across attempts. */
  minutes: number;
  /** The sheet holding the working for this task. */
  sheetId: string;
  /** What went wrong, in the owner's words. The single most valuable field in the app. */
  mistake: string;
  createdAt: number;
  updatedAt: number;
}

export type LogKind = 'opened' | 'done' | 'wrong' | 'stuck' | 'note' | 'chapter' | 'course';

export interface LogEntry {
  id: string;
  at: number;
  kind: LogKind;
  courseId: string;
  chapterId: string;
  taskId: string;
  /** Denormalised so the master log renders without loading every course and chapter. */
  label: string;
  detail: string;
  minutes: number;
}

// ── Task numbers ───────────────────────────────────────────────────────────────────────────

/**
 * The numeric key for a task number.
 *
 * `1001` is 1001. `4.2.13` becomes 4_002_013, so section numbering sorts correctly and still
 * fits a range test. Anything unparseable sorts last rather than throwing.
 */
export function taskIndex(number: string): number {
  const parts = number.trim().split(/[.:\-\s]+/).filter(Boolean);
  if (parts.length === 0) return Number.MAX_SAFE_INTEGER;
  if (parts.length === 1) {
    const value = Number(parts[0]);
    return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
  }
  return parts.reduce((total, part) => {
    const value = Number(part);
    return total * 1000 + (Number.isFinite(value) ? value : 0);
  }, 0);
}

export const inChapter = (chapter: Chapter, number: string): boolean => {
  const index = taskIndex(number);
  return index >= chapter.from && index <= chapter.to;
};

/** How many exercises a chapter covers, which is what the progress ring is a fraction of. */
export const chapterSize = (chapter: Chapter): number =>
  Math.max(0, chapter.to - chapter.from + 1);

/**
 * Every chapter claiming a number, the course in focus first.
 *
 * Two courses numbering their exercises the same way is normal — Ma4's chapter 1 runs 1001–1143
 * and a linear algebra chapter 10 numbered 1001–1060 is just as legitimate — so a number alone
 * has never been enough to identify a chapter. Resolving without the course in focus is what put
 * a LinAlg exercise on a hub captioned "Matematik 4".
 */
export const chaptersFor = (
  chapters: readonly Chapter[],
  number: string,
  courseId?: string,
): Chapter[] => {
  const claiming = chapters.filter(chapter => inChapter(chapter, number));
  if (!courseId) return claiming;
  const here = claiming.filter(chapter => chapter.courseId === courseId);
  return here.length > 0 ? here : claiming;
};

export interface ChapterMatch {
  /** The best answer, or null when no chapter covers the number at all. */
  chapter: Chapter | null;
  /** Every chapter that claims the number, across all courses. */
  claiming: Chapter[];
  /**
   * True when the number is claimed twice INSIDE the course being resolved against. That is a
   * data error rather than an ordinary collision, and a surface should say so instead of
   * pretending the first row won fairly.
   */
  ambiguous: boolean;
}

/**
 * Resolve a number to a chapter, and report what it had to choose between.
 *
 * The tie-break is the narrowest range, then the lowest order: if two chapters in one course
 * both claim 1042, the one covering 1001–1060 is a better answer than the one covering
 * 1001–9999, and the choice is at least deterministic rather than array order.
 */
export function resolveChapter(
  chapters: readonly Chapter[],
  number: string,
  courseId?: string,
): ChapterMatch {
  const claiming = chapters.filter(chapter => inChapter(chapter, number));
  const scoped = courseId ? claiming.filter(chapter => chapter.courseId === courseId) : claiming;
  const pool = scoped.length > 0 ? scoped : claiming;
  const best = [...pool].sort((a, b) => {
    const span = chapterSize(a) - chapterSize(b);
    return span !== 0 ? span : a.order - b.order;
  })[0] ?? null;
  return { chapter: best, claiming, ambiguous: scoped.length > 1 };
}

/**
 * The chapter a number belongs to, if any — so typing "1042" jumps straight there.
 *
 * `courseId` is optional so existing callers keep working, but passing the course the user is
 * looking at is what makes the answer right when two courses number their books alike.
 */
export const chapterFor = (
  chapters: readonly Chapter[],
  number: string,
  courseId?: string,
): Chapter | null => resolveChapter(chapters, number, courseId).chapter;

/**
 * The next task number to open after this one.
 *
 * It is deliberately NOT "the next integer": the owner skips around, so the next number they
 * have already touched is more useful than one they have not. Untouched numbers come after.
 */
export function nextNumber(
  chapter: Chapter,
  current: string,
  touched: readonly Task[],
): string | null {
  const here = taskIndex(current);
  const ahead = touched
    .filter(task => task.index > here && task.index <= chapter.to && task.status !== 'done')
    .sort((a, b) => a.index - b.index)[0];
  if (ahead) return ahead.number;
  const next = here + 1;
  return next <= chapter.to ? String(next) : null;
}

export function previousNumber(chapter: Chapter, current: string): string | null {
  const previous = taskIndex(current) - 1;
  return previous >= chapter.from ? String(previous) : null;
}

// ── Progress ───────────────────────────────────────────────────────────────────────────────

export interface Progress {
  touched: number;
  done: number;
  wrong: number;
  stuck: number;
  total: number;
  minutes: number;
}

export function progressOf(chapter: Chapter, tasks: readonly Task[]): Progress {
  const mine = tasks.filter(task => task.chapterId === chapter.id);
  return {
    touched: mine.length,
    done: mine.filter(task => task.status === 'done').length,
    wrong: mine.filter(task => task.status === 'wrong').length,
    stuck: mine.filter(task => task.status === 'stuck').length,
    total: chapterSize(chapter),
    minutes: mine.reduce((sum, task) => sum + task.minutes, 0),
  };
}

// ── Seeds ──────────────────────────────────────────────────────────────────────────────────

const id = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const newCourse = (course: Partial<Course> & Pick<Course, 'code' | 'name' | 'kind' | 'book' | 'accent' | 'order'>): Course => ({
  id: id('course'),
  createdAt: Date.now(),
  ...course,
});

export const newChapter = (
  courseId: string,
  number: string,
  title: string,
  from: number,
  to: number,
  order: number,
): Chapter => ({
  id: id('chapter'), courseId, number, title, from, to, order, createdAt: Date.now(),
});

export function newTask(chapter: Chapter, number: string): Task {
  const now = Date.now();
  return {
    id: id('task'),
    chapterId: chapter.id,
    courseId: chapter.courseId,
    number,
    index: taskIndex(number),
    title: '',
    status: 'doing',
    tier: '',
    minutes: 0,
    sheetId: `sheet-${chapter.id}-${number}`,
    mistake: '',
    createdAt: now,
    updatedAt: now,
  };
}

export const newLogEntry = (entry: Omit<LogEntry, 'id' | 'at'> & { at?: number }): LogEntry => ({
  id: id('log'),
  at: entry.at ?? Date.now(),
  ...entry,
});

/**
 * The four courses the owner is taking. Chapters are deliberately NOT seeded: the book's chapter
 * titles and ranges are something they know and can enter in seconds, and a wrong seed is worse
 * than an empty one because it has to be found and corrected before it can be trusted.
 */
export const SEED_COURSES: Course[] = [
  newCourse({ code: 'Ma4', name: { sv: 'Matematik 4', en: 'Mathematics 4' }, kind: 'gymnasium', book: 'Matematik 5000+ 4', accent: 1, order: 1 }),
  newCourse({ code: 'Ma5', name: { sv: 'Matematik 5', en: 'Mathematics 5' }, kind: 'gymnasium', book: 'Matematik 5000+ 5', accent: 2, order: 2 }),
  newCourse({ code: 'LinAlg', name: { sv: 'Linjär algebra', en: 'Linear algebra' }, kind: 'university', book: '', accent: 3, order: 3 }),
  newCourse({ code: 'Algebra', name: { sv: 'Algebrans grunder', en: 'Foundations of algebra' }, kind: 'university', book: '', accent: 4, order: 4 }),
];
